import { Button, FieldBlock, Input, Modal, Select, Textarea } from '@orbind/ui'
import { useMemo, useState } from 'react'
import type { Bootstrap } from '../../lib/catalog.ts'
import { useT } from '../../lib/i18n.tsx'
import { useEtapasIniciais } from './etapas-iniciais.ts'
import { escolhaValida, escolhasLembradas, lembrarEscolhas } from './lembrar-conversao.ts'
import './converter-item.css'

/**
 * "Converter em item" (pedido 2026-08-30): antes de virar Item de Trabalho, a
 * linha do checklist pergunta TIPO, STATUS, ETAPA e DESCRIÇÃO.
 *
 * A etapa segue a regra do resto do sistema: tipo que participa de fluxo(s) só
 * aceita etapa DESSES fluxos e a lista mostra as ETAPAS INICIAIS (o item está
 * nascendo); tipo sem fluxo pode ficar sem etapa.
 */
export function ConverterItemModal({
  texto,
  bootstrap,
  convertendo,
  onConfirmar,
  onFechar,
}: {
  /** Texto da linha — vira o título do item. */
  texto: string
  bootstrap: Bootstrap
  convertendo: boolean
  onConfirmar: (dados: {
    workItemTypeId: string
    workItemStatusId: string
    workItemPriorityId: string
    companyAreaId: string | null
    flowStepId: string | null
    description: string
  }) => void
  onFechar: () => void
}) {
  const t = useT()
  const rotulo = (row: { code: string; nameTranslationKey?: string | null }) => {
    const chave = row.nameTranslationKey ?? `x.${row.code}`
    const label = t(chave)
    return label === chave ? row.code : label
  }

  const tipos = (bootstrap.workItemTypes ?? []).filter((x) => x.isActive)
  const status = (bootstrap.workItemStatuses ?? []).filter((s) => s.isActive)
  const statusPadrao = status.find((s) => s.isInitial) ?? status.find((s) => !s.isTerminal) ?? status[0]

  const prioridades = bootstrap.priorities ?? []
  // do meio da lista (a "média" das seeds), como no resto do sistema
  const prioridadePadrao = prioridades[Math.floor(prioridades.length / 2)] ?? prioridades[0]
  const areas = bootstrap.areas ?? []

  /*
   * As escolhas da última conversão (SPEC-20260918-2146): quem converte uma
   * linha quase nunca converte só uma, e quase sempre repete tipo, status e
   * área. Cada campo cai no padrão de sempre quando não há nada lembrado — ou
   * quando o que estava lembrado saiu do catálogo.
   */
  const lembrado = useMemo(() => escolhasLembradas(), [])
  const [tipoId, setTipoId] = useState(() => escolhaValida(lembrado.workItemTypeId, tipos, tipos[0]?.id ?? ''))
  const [statusId, setStatusId] = useState(() =>
    escolhaValida(lembrado.workItemStatusId, status, statusPadrao?.id ?? ''),
  )
  const [prioridadeId, setPrioridadeId] = useState(() =>
    escolhaValida(lembrado.workItemPriorityId, prioridades, prioridadePadrao?.id ?? ''),
  )
  const [areaId, setAreaId] = useState(() => escolhaValida(lembrado.companyAreaId, areas, ''))
  // a etapa depende do TIPO, então é conferida contra as etapas dele mais abaixo
  const [etapaId, setEtapaId] = useState(lembrado.flowStepId ?? '')
  // a descrição NÃO se lembra: é conteúdo daquele item, não preferência
  const [descricao, setDescricao] = useState('')

  /*
   * As PORTAS DE ENTRADA do fluxo, pela mesma regra do resto do sistema
   * (SPEC-20260918-2341).
   *
   * Esta tela tinha uma cópia própria da regra, e a cópia não acompanhou o
   * "pode iniciar por esta etapa" que o desenho do fluxo ganhou em 2026-09-02:
   * oferecia UMA etapa por fluxo, mesmo quando o fluxo declarava várias.
   */
  const etapasIniciais = useEtapasIniciais(tipoId, tipos)

  const tipoTemFluxo = etapasIniciais.length > 0
  /*
   * A etapa lembrada só vale se for uma das etapas iniciais DO TIPO escolhido —
   * ela é o único campo que depende de outro. Trocar o tipo troca o conjunto de
   * etapas, e uma etapa de outro fluxo aqui seria dado inválido.
   */
  const etapaLembradaServe = etapasIniciais.some((n) => n.flowStepId === etapaId)
  // com um fluxo só, já vem preenchida — não há o que escolher
  const etapaEfetiva = tipoTemFluxo
    ? (etapaLembradaServe ? etapaId : '') || etapasIniciais[0]!.flowStepId
    : ''
  const podeConverter = tipoId !== '' && statusId !== '' && prioridadeId !== '' && !convertendo

  return (
    <Modal
      open
      title="Converter em Item de Trabalho"
      onClose={onFechar}
      width={560}
      footer={
        <>
          <Button variant="ghost" onClick={onFechar}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={!podeConverter}
            onClick={() => {
              // guarda ANTES de converter: se a conversão falhar, a próxima
              // tentativa já vem com o que a pessoa escolheu, sem redigitar
              lembrarEscolhas({
                workItemTypeId: tipoId,
                workItemStatusId: statusId,
                workItemPriorityId: prioridadeId,
                companyAreaId: areaId,
                flowStepId: etapaEfetiva,
              })
              onConfirmar({
                workItemTypeId: tipoId,
                workItemStatusId: statusId,
                workItemPriorityId: prioridadeId,
                companyAreaId: areaId || null,
                flowStepId: etapaEfetiva || null,
                description: descricao.trim(),
              })
            }}
          >
            {convertendo ? 'Convertendo…' : 'Converter'}
          </Button>
        </>
      }
    >
      <div className="cvt">
        <FieldBlock label="Título" hint="Vem do texto da linha do checklist.">
          {({ id }) => <Input id={id} value={texto} readOnly />}
        </FieldBlock>

        <div className="cvt__linha">
          <FieldBlock label="Tipo">
            {({ id }) => (
              <Select
                id={id}
                value={tipoId}
                options={tipos.map((x) => ({ value: x.id, label: rotulo(x), icon: x.icon ?? undefined }))}
                onChange={(v) => {
                  setTipoId(v)
                  // o fluxo depende do tipo: trocar de tipo zera a etapa
                  setEtapaId('')
                }}
              />
            )}
          </FieldBlock>
          <FieldBlock label="Status">
            {({ id }) => (
              <Select
                id={id}
                value={statusId}
                options={status.map((s) => ({ value: s.id, label: rotulo(s) }))}
                onChange={setStatusId}
              />
            )}
          </FieldBlock>
        </div>

        <div className="cvt__linha">
          <FieldBlock label="Prioridade">
            {({ id }) => (
              <Select
                id={id}
                value={prioridadeId}
                options={prioridades.map((p) => ({ value: p.id, label: rotulo(p) }))}
                onChange={setPrioridadeId}
              />
            )}
          </FieldBlock>
          <FieldBlock label="Área">
            {({ id }) => (
              <Select
                id={id}
                value={areaId}
                options={[{ value: '', label: 'Sem área' }, ...areas.map((a) => ({ value: a.id, label: rotulo(a) }))]}
                onChange={setAreaId}
              />
            )}
          </FieldBlock>
        </div>

        <FieldBlock
          label="Etapa"
          hint={
            !tipoTemFluxo
              ? 'Este tipo não participa de nenhum fluxo — o item nasce sem etapa.'
              : etapasIniciais.length > 1
                ? 'Por onde o item entra no fluxo — escolha entre as etapas que podem iniciar.'
                : 'Este tipo segue fluxo: o item começa pela etapa inicial.'
          }
        >
          {({ id }) =>
            tipoTemFluxo ? (
              <Select
                id={id}
                value={etapaEfetiva}
                options={etapasIniciais.map((n) => ({ value: n.flowStepId, label: `${n.stepName} · ${n.flowName}` }))}
                onChange={setEtapaId}
              />
            ) : (
              <Input id={id} value="— Sem etapa" readOnly />
            )
          }
        </FieldBlock>

        <FieldBlock label="Descrição" hint="Opcional.">
          {({ id }) => (
            <Textarea
              id={id}
              rows={4}
              value={descricao}
              placeholder="O que precisa ser feito?"
              onChange={(e) => setDescricao(e.target.value)}
            />
          )}
        </FieldBlock>
      </div>
    </Modal>
  )
}
